import { FieldArray, Formik, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import apiRequest from "../../../utils/admin/apiRequest";
import InputField from "../../ui/form/InputField";
import Button from "../../ui/utils/Button";
import SelectField from "../../ui/form/SelectField";
import usePost from "../../../hooks/api/usePost";
import Alarm from "../../ui/utils/Alert";
import useFetch from "../../../hooks/api/useFetch";
import { updateVendor } from "../../../validation/vendor/authValidation";
import { CgAdd } from "react-icons/cg";
import { Trash } from "lucide-react";
import AsyncSearchSelectProfile from "../../ui/form/AsyncSearchSelectProfile";

// ----------------------------------------
// LOCATION WATCHER
// ----------------------------------------
const LocationWatcher = ({
  fetchStates,
  fetchCities,
  setBlankResponse,
  setCityBlankResponse,
}) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.countryId) {
      fetchStates({ countryId: values.countryId });
      setFieldValue("stateId", "");
    } else {
      setBlankResponse();
    }
  }, [values.countryId]);

  useEffect(() => {
    if (values.stateId) {
      fetchCities({ stateId: values.stateId });
      setFieldValue("cityId", "");
    } else {
      setCityBlankResponse();
    }
  }, [values.stateId]);

  return null;
};



// ----------------------------------------
// EDIT VENDOR MAIN COMPONENT
// ----------------------------------------
const EditVendor = ({ data, closeModal, callback }) => {
  const { error, isLoading: loading, post, serverErrors } = usePost(
    apiRequest.editVendor
  );


  // LOAD admin list
  const { data: adminList } = useFetch({
    apiMethod: apiRequest.getHeadAdmin,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: true,
  });

  // corporate admin list fetcher
  const { data: towerAdmins, refetch: fetchTowerAdmins, setBlankResponse: clearTowerAdmins } =
    useFetch({
      apiMethod: apiRequest.getCorporateAdminByHeadAdmin,
      autoFetch: false,
    });

  // corporate codes fetcher
  const { data: towerCodes, refetch: fetchTowerCodes, setBlankResponse: clearTowerCodes } =
    useFetch({
      apiMethod: apiRequest.getCorporateCodesByCorporateAdmin,
      autoFetch: false,
    });

  // Location Fetch
  const { data: countryData } = useFetch({
    apiMethod: apiRequest.getCountry,
    body: { paginate: false },
  });

  const { data: stateData, refetch: fetchStates, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getStateByCountryId,
    autoFetch: false,
  });

  const {
    data: cityData,
    refetch: fetchCities,
    setBlankResponse: setCityBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCityByState,
    autoFetch: false,
  });


  const v = data;

  console.log(v);

  // ----------------------------------------
  // INITIAL VALUES FROM DATABASE
  // ----------------------------------------

  const [selectedCorporateOptions, setSelectedCorporateOptions] = useState([]);

  const initialValues = {
    name: v.name || "",
    countryId: v?.countryId?._id || "",
    stateId: v.stateId?._id || "",
    cityId: v.cityId?._id || "",
    mobile: v.mobile || "",
    email: v.email || "",
    contactPerson: v.contactPerson || "",
    userId: v.userId || "",
    password: "", // password optional during update
    vendorType: v.vendorType,
    isSuperVendor: v.isSuperVendor,
    addedBy: v.addedBy || null,
    displayName: "superVendor",


    userDetails: v.userDetails?.length ? v.userDetails : [
      { name: "", mobile: "", email: "" },
    ],

    corporateCodeId: data?.corporateCodeId?.length
      ? data.corporateCodeId.map((c) => c._id)
      : [""],
  };

  useEffect(() => {
    if (data?.corporateCodeId?.length) {
      setSelectedCorporateOptions(
        data.corporateCodeId.map((c) => ({
          label: `${c.corporateName} (${c.corporateCode})`,
          value: c._id,
        }))
      );
    }
  }, [data]);

  const fetchCorporateCodes = async (search) => {
    const res = await apiRequest.getCorporateCodeForSelect({
      body: { filters: { name: { $regex: search, $options: "i" }, }, paginate: false, },
    });

    return (res?.data?.data?.corporatecodes?.map((v) => ({
      label: `${v.name} (${v.code})`,
      value: v.id,
    })) || []);
  };


  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Edit Vendor Profile</h2>
      <div className="border-b" />

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={updateVendor}
        onSubmit={async (values) => {
          const response = await post({ data: { body: values, id: data?._id } });

          if (response.response) callback?.();
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, touched, errors ,setFieldValue}) => (
          <>
            <LocationWatcher
              fetchStates={fetchStates}
              fetchCities={fetchCities}
              setBlankResponse={setBlankResponse}
              setCityBlankResponse={setCityBlankResponse}
            />

          

            <form onSubmit={handleSubmit} className="text-sm max-h-[80vh] overflow-auto">
              {error && <Alarm message={error} type="error" />}

              {/* ------------------------------ MAIN GRID START ------------------------------ */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-4">

                <InputField
                  name="name"
                  label="Vendor Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && errors.name}
                  serverError={serverErrors?.name}
                />

                <InputField
                  name="mobile"
                  value={values.mobile}
                  label="Mobile"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={touched.mobile && errors.mobile}
                  serverError={serverErrors?.mobile}
                />

                <InputField
                  name="email"
                  value={values.email}
                  label="Email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={touched.email && errors.email}
                  serverError={serverErrors?.email}
                />

                <SelectField
                  name="countryId"
                  value={values.countryId}
                  label="Country"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={countryData?.countries?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.countryId && errors.countryId}
                  serverError={serverErrors?.countryId}
                />

                <SelectField
                  name="stateId"
                  value={values.stateId}
                  label="State"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={stateData?.states?.map((s) => ({
                    value: s._id,
                    label: s.name,
                  }))}
                  error={touched.stateId && errors.stateId}
                  serverError={serverErrors?.stateId}
                />

                <SelectField
                  name="cityId"
                  value={values.cityId}
                  label="City"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={cityData?.cities?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.cityId && errors.cityId}
                  serverError={serverErrors?.cityId}
                />

                <InputField
                  name="contactPerson"
                  label="Contact Person"
                  value={values.contactPerson}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contactPerson && errors.contactPerson}
                  serverError={serverErrors?.contactPerson}
                />

                <InputField
                  name="userId"
                  label="User ID"
                  value={values.userId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.userId && errors.userId}
                  serverError={serverErrors?.userId}
                />

                <InputField
                  type="password"
                  name="password"
                  label="Password (optional)"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && errors.password}
                  serverError={serverErrors?.password}
                />


                <div className="col-span-full">
                  <h3 className="font-semibold mb-2">Partner Details</h3>

                  <FieldArray name="userDetails">
                    {({ push, remove }) => (
                      <>
                        <table className="w-full border">
                          <thead>
                            <tr>
                              <th className="p-2 border">Name</th>
                              <th className="p-2 border">Mobile</th>
                              <th className="p-2 border">Email</th>
                              <th className="p-2 border text-center">Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            {values.userDetails.map((row, index) => (
                              <tr key={index}>

                                {/* NAME */}
                                <td className="p-2 border">
                                  <InputField
                                    name={`userDetails.${index}.name`}
                                    value={row.name}
                                    placeholder="Enter name"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      touched.userDetails?.[index]?.name &&
                                      errors.userDetails?.[index]?.name
                                    }
                                    serverError={serverErrors?.[`userDetails.${index}.name`]}
                                  />
                                </td>

                                {/* MOBILE */}
                                <td className="p-2 border">
                                  <InputField
                                    name={`userDetails.${index}.mobile`}
                                    value={row.mobile}
                                    onChange={handleChange}
                                    placeholder="Enter mobile"

                                    onBlur={handleBlur}
                                    error={
                                      touched.userDetails?.[index]?.mobile &&
                                      errors.userDetails?.[index]?.mobile
                                    }
                                    serverError={serverErrors?.[`userDetails.${index}.mobile`]}
                                  />
                                </td>

                                {/* EMAIL */}
                                <td className="p-2 border">
                                  <InputField
                                    name={`userDetails.${index}.email`}
                                    value={row.email}
                                    onChange={handleChange}
                                    placeholder="Enter email"

                                    onBlur={handleBlur}
                                    error={
                                      touched.userDetails?.[index]?.email &&
                                      errors.userDetails?.[index]?.email
                                    }
                                    serverError={serverErrors?.[`userDetails.${index}.email`]}
                                  />
                                </td>

                                {/* REMOVE BUTTON */}
                                <td className="text-center p-2 border">
                                  <button
                                    type="button"
                                    onClick={() => remove(index)}
                                    className="text-red-500"
                                  >
                                    <Trash size={17} />
                                  </button>
                                </td>

                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() =>
                              push({ name: "", mobile: "", email: "" })
                            }
                            className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded flex items-center gap-2"
                          >
                            <CgAdd /> Add User
                          </button>
                        </div>
                      </>
                    )}
                  </FieldArray>

                </div>

                <div className="col-span-full">
                  <FieldArray name="corporateCodeId">
                    {({ push, remove }) => (
                      <>
                        {values.corporateCodeId.map((_, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 mb-2"
                          >
                            <AsyncSearchSelectProfile
                              placeholder="Select Corporate Code"
                              value={selectedCorporateOptions[index] || null}
                              fetchOptions={fetchCorporateCodes}
                              onChange={(option) => {
                                setFieldValue(`corporateCodeId.${index}`, option?.value || "");

                                const updated = [...selectedCorporateOptions];
                                updated[index] = option;
                                setSelectedCorporateOptions(updated);
                              }}
                            />


                            <button
                              type="button"
                              onClick={() => {
                                remove(index);
                                setSelectedCorporateOptions((prev) =>
                                  prev.filter((_, i) => i !== index)
                                );
                              }}
                              className="text-red-500"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={() => {
                            push("");
                            setSelectedCorporateOptions((prev) => [
                              ...prev,
                              null,
                            ]);
                          }}
                          className="mt-2 px-2 py-1 bg-blue-500 text-white text-xs rounded flex items-center gap-2"
                        >
                          <CgAdd /> Add Corporate Code
                        </button>
                      </>
                    )}
                  </FieldArray>
                </div>

              </div>

              <div className="flex justify-end gap-2 p-4 border-t">
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={loading}>
                  Update Vendor
                </Button>
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default EditVendor;
